import { init, initData, miniApp, openTelegramLink, viewport } from '@telegram-apps/sdk-react'
import type { User } from '@telegram-apps/sdk-react'

import { hasExplicitThemePref } from './theme'

/**
 * Telegram Mini App bootstrap.
 *
 * Every exported function here is safe to call outside Telegram (plain browser during
 * development): the SDK throws when the native `window.Telegram.WebApp` bridge / launch
 * params are missing, so each call is wrapped in try/catch and degrades to a no-op / null
 * instead of crashing the app.
 */

let insideTelegram = false

/** True once `initTelegram()` has confirmed real Telegram launch/init data is present. */
export function isInsideTelegram(): boolean {
  return insideTelegram
}

/**
 * Initialises the SDK, restores init data, mounts the mini app + viewport components and
 * expands the viewport to full height. Call once, before rendering the app.
 */
export async function initTelegram(): Promise<void> {
  try {
    init()
  } catch {
    // Not running inside Telegram — nothing else to do.
    return
  }

  try {
    initData.restore()
  } catch {
    // No launch params available.
  }

  try {
    if (!miniApp.isMounted()) {
      await miniApp.mount()
    }
    miniApp.ready()
  } catch {
    // miniApp component unsupported in this environment.
  }

  try {
    if (!viewport.isMounted()) {
      await viewport.mount()
    }
    viewport.expand()
  } catch {
    // viewport component unsupported in this environment.
  }

  applyTelegramTheme()

  insideTelegram = Boolean(getInitDataRaw())
}

/**
 * Telegram temasiga qarab `<html data-theme="dark|light">` qo'yadi.
 *
 * Nega kerak: brend rangi qorong'i temada boshqa (yorugroq) qiymat oladi,
 * aks holda `#22453F` fon bilan qo'shilib ketadi (kontrast 1.54:1).
 * Faqat `prefers-color-scheme` ga tayanib bo'lmaydi — Telegram temasi tizim
 * temasiga ergashmasligi mumkin (foydalanuvchi ilova ichida alohida tanlaydi).
 *
 * `index.css` shu atributga ham, media so'rovga ham javob beradi.
 */
function applyTelegramTheme(): void {
  // Foydalanuvchi temani qo'lda tanlagan bo'lsa (light/dark), avtomatik derivatsiya uni
  // bosib o'tmasligi kerak — tanlov ustun (index.html inline skripti allaqachon qo'ygan).
  if (hasExplicitThemePref()) return

  try {
    const bg = miniApp.backgroundColorRGB()
    if (bg === undefined) return

    // Yorqinlikni oddiy usulda baholaymiz: to'q fon -> qorong'i tema.
    const value = parseInt(bg.replace('#', ''), 16)
    const [r, g, b] = [(value >> 16) & 255, (value >> 8) & 255, value & 255]
    const luminance = (0.2126 * r + 0.7152 * g + 0.0722 * b) / 255

    document.documentElement.dataset.theme = luminance < 0.5 ? 'dark' : 'light'
  } catch {
    // Telegram tashqarisida yoki SDK qo'llab-quvvatlamasa — `prefers-color-scheme`
    // ishlayveradi, `data-theme` umuman qo'yilmaydi.
  }
}

/** Raw `initData` query string — sent as the `Authorization: tma <raw>` header by the API client. */
export function getInitDataRaw(): string | null {
  try {
    return initData.raw() ?? null
  } catch {
    return null
  }
}

/** Parsed Telegram user from init data, or null outside Telegram / before init completes. */
export function getTelegramUser(): User | null {
  try {
    return initData.user() ?? null
  } catch {
    return null
  }
}

/**
 * Arxiv kanaldagi video havolasini ochish (`t.me/c/<kanal>/<xabar>`, `VideoDetail.archiveUrl`,
 * `docs/03-app-flow.md`: "[▶️ Telegram'da ochish]").
 *
 * Panel Telegram Mini App ICHIDA ishlaydi — oddiy `<a href>` yoki `window.open()` bilan `t.me`
 * havolasini ochish ba'zi Telegram mijozlarida hech narsa qilmasligi yoki panelning holatini
 * buzishi mumkin. SDK'ning `openTelegramLink()` funksiyasi bunday havolalarni Telegram'ning
 * o'z native UI'si orqali ochish uchun mo'ljallangan — aynan shu ishlatiladi, oddiy tashqi
 * havolalar uchun ishlatiladigan `openLink()` emas (u umuman `t.me` domenini kutmaydi).
 *
 * Telegram tashqarisida (brauzerda dev/test qilinganda) `openTelegramLink` mavjud emas —
 * bunday holda yangi tabda ochiladi, aks holda tugma sababsiz "ishlamayotganday" ko'rinadi.
 */
export function openArchiveLink(url: string): void {
  try {
    if (openTelegramLink.isAvailable()) {
      openTelegramLink(url)
      return
    }
  } catch {
    // SDK ishga tushmagan / noma'lum muhit — pastdagi brauzer zaxirasiga o'tamiz.
  }
  window.open(url, '_blank', 'noopener,noreferrer')
}

/**
 * Closes the Mini App — used by the "sessiya tugadi" error screen so the admin can return to
 * the bot and reopen the panel. No-op outside Telegram (the method is simply unavailable there).
 */
export function closeMiniApp(): void {
  try {
    if (miniApp.close.isAvailable()) {
      miniApp.close()
    }
  } catch {
    // Not running inside Telegram / component not mounted — nothing to do.
  }
}
